import React, { useRef, useState, useEffect } from 'react';
import { View, Text, Image, TouchableOpacity, ActivityIndicator } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { AdminColors } from '../../../../../Admin/constants/adminColors';
import SectionCard from '../SectionCard';
import SignatureDisclaimer from '../../../../../../components/SignatureDisclaimer';
import {
  SignatureCapture,
  SignatureData,
  Signer,
  ActivityType,
} from '../../types/beginActivity.common.types';
import { uploadDataUrlImage } from '../../uploadImage';
import { FinalSignaturesProps } from './FinalSignatures.types';
import SignatureCanvas from './SignatureCanvas.tsx';
import { SignatureCanvasHandle } from './SignatureCanvas.types';
import styles from './FinalSignatures.styles';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function activityLabel(activityType: ActivityType): string {
  switch (activityType) {
    case 'pulse_check':         return 'Pulse Check';
    case 'full_observation':    return 'Full Observation';
    case 'routine_observation': return 'Routine Observation';
  }
}

function formatTimestamp(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// ─── Single signature card ────────────────────────────────────────────────────
interface SigCardProps {
  signer: Signer;
  isEmployee: boolean;
  signature: SignatureCapture | undefined;
  isReadOnly: boolean;
  onSign: (capture: SignatureCapture) => void;
  onClear: () => void;
  onUploadingChange: (isUploading: boolean) => void;
}

function SigCard({ signer, isEmployee, signature, isReadOnly, onSign, onClear, onUploadingChange }: SigCardProps) {
  const isSigned = !!(signature?.imageData || signature?.imageUrl);
  const canvasRef = useRef<SignatureCanvasHandle>(null);
  const [hasInk, setHasInk] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const handleClearPad = () => {
    canvasRef.current?.clear();
    setHasInk(false);
  };

  const handleAcceptSignature = async () => {
    const dataUrl = await canvasRef.current?.capture();
    if (!dataUrl) return;
    const signedOn = new Date().toISOString();
    onSign({ name: signer.expectedName, signedOn, imageData: dataUrl });
    setHasInk(false);

    // Upload the captured signature the same way the Observation KFA camera/gallery
    // flow does, then attach the resulting imageUrl so it can be sent in the payload.
    setIsUploading(true);
    onUploadingChange(true);
    try {
      const uploaded = await uploadDataUrlImage(dataUrl, `${signer.role.toLowerCase()}-signature.png`);
      if (uploaded) {
        onSign({ name: signer.expectedName, signedOn, imageData: dataUrl, ...uploaded });
      }
    } catch {
      // Signature still stands locally (imageData) even if the upload failed.
    } finally {
      setIsUploading(false);
      onUploadingChange(false);
    }
  };

  if (isSigned) {
    return (
      <View style={styles.sigCardSigned}>
        <View style={styles.sigCheckCircle}>
          <MaterialIcons name="check" size={16} color={AdminColors.textOnDark} />
        </View>
        <View style={styles.sigSignedCopy}>
          <Text style={styles.sigSignedLabel}>{`${signer.role} signature / ${signature?.name ?? signer.expectedName}`}</Text>
          {signature?.imageUrl || signature?.imageData ? (
            <Image
              style={styles.signedImage}
              source={{ uri: signature.imageUrl ?? signature.imageData }}
              accessibilityLabel={`${signer.role} signature`}
            />
          ) : (
            <Text style={styles.sigSignedName}>{signature?.name ?? signer.expectedName}</Text>
          )}
          {signature?.signedOn && (
            <Text style={styles.sigSignedMeta}>{`Signed ${formatTimestamp(signature.signedOn)}`}</Text>
          )}
          {isUploading && (
            <View style={styles.uploadingRow}>
              <ActivityIndicator size="small" color={AdminColors.blue700} />
              <Text style={styles.sigSignedMeta}>Uploading signature…</Text>
            </View>
          )}
        </View>
        {!isReadOnly && (
          <TouchableOpacity
            style={styles.replaceButton}
            disabled={isUploading}
            onPress={onClear}
            accessibilityLabel={`Replace ${signer.role} signature`}
          >
            <Text style={styles.replaceButtonText}>Replace</Text>
          </TouchableOpacity>
        )}
      </View>
    );
  }

  return (
    <View style={styles.sigCard}>
      <View style={styles.sigInfoCol}>
        <Text style={styles.sigRoleLabel}>{`${signer.role} signature`}</Text>
        <Text style={styles.sigName}>{signer.expectedName}</Text>
        <Text style={styles.sigInstruction}>
          Sign inside the box with a finger, stylus, or mouse.
        </Text>
      </View>
      <View style={styles.sigPadCol}>
        <View style={styles.canvasWrapper}>
          <SignatureCanvas ref={canvasRef} disabled={isReadOnly} onHasContentChange={setHasInk} />
        </View>
        <View style={styles.padActions}>
          <TouchableOpacity
            style={[styles.padButton, (!hasInk || isReadOnly) && styles.padButtonDisabled]}
            onPress={handleClearPad}
            disabled={!hasInk || isReadOnly}
            accessibilityLabel={`Clear ${signer.role} signature pad`}
          >
            <Text style={styles.padButtonText}>Clear</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.padButton, styles.padButtonPrimary, (!hasInk || isReadOnly) && styles.padButtonDisabled]}
            onPress={handleAcceptSignature}
            disabled={!hasInk || isReadOnly}
            accessibilityLabel={`Accept ${signer.role} signature`}
          >
            <Text style={[styles.padButtonText, styles.padButtonTextPrimary]}>Accept Signature</Text>
          </TouchableOpacity>
        </View>
        {isEmployee && !isReadOnly && (
          <SignatureDisclaimer disabled={isReadOnly} />
        )}
      </View>
    </View>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
const FinalSignatures: React.FC<FinalSignaturesProps> = ({
  signatureData,
  onSignatureChange,
  signers,
  activityType,
  isReadOnly = false,
  onUploadingChange,
  bare = false,
}) => {
  const [employeeSigner, managerSigner] = signers;
  const requiredCount = managerSigner ? 2 : 1;
  const { employeeSignature, managerSignature } = signatureData;

  const [isEmployeeUploading, setIsEmployeeUploading] = useState(false);
  const [isManagerUploading, setIsManagerUploading] = useState(false);

  useEffect(() => {
    onUploadingChange?.(isEmployeeUploading || isManagerUploading);
  }, [isEmployeeUploading, isManagerUploading, onUploadingChange]);

  const signedCount =
    (employeeSignature?.imageData || employeeSignature?.imageUrl ? 1 : 0) +
    (managerSignature?.imageData || managerSignature?.imageUrl ? 1 : 0);
  const allSigned = signedCount === 2;
  const label = activityLabel(activityType);

  const handleSign = (key: keyof SignatureData, capture: SignatureCapture) =>
    onSignatureChange({ ...signatureData, [key]: capture });

  const handleClear = (key: keyof SignatureData) =>
    onSignatureChange({ ...signatureData, [key]: undefined });

  const signatureCards = (
    <View style={styles.signatureStack}>
      <SigCard
        signer={employeeSigner}
        isEmployee
        signature={employeeSignature}
        isReadOnly={isReadOnly}
        onSign={(c) => handleSign('employeeSignature', c)}
        onClear={() => handleClear('employeeSignature')}
        onUploadingChange={setIsEmployeeUploading}
      />
      {managerSigner && (
        <SigCard
          signer={managerSigner}
          isEmployee={false}
          signature={managerSignature}
          isReadOnly={isReadOnly}
          onSign={(c) => handleSign('managerSignature', c)}
          onClear={() => handleClear('managerSignature')}
          onUploadingChange={setIsManagerUploading}
        />
      )}
    </View>
  );

  if (bare) return signatureCards;

  return (
    <SectionCard
      title="Final Signatures"
      subtitle={
        <View
          style={[
            styles.signedBadge,
            allSigned ? styles.signedBadgeComplete : styles.signedBadgePending,
          ]}
        >
          <Text
            style={[
              styles.signedBadgeText,
              allSigned ? styles.signedBadgeTextComplete : styles.signedBadgeTextPending,
            ]}
          >
            {signedCount}/{requiredCount} signed
          </Text>
        </View>
      }
    >
      <View style={styles.body}>
        {/* Intro copy */}
        <Text style={styles.introCopy}>
          {managerSigner
            ? `Both people sign the same completed conversation before the ${label} can be finalized.`
            : `The employee signs before the ${label} can be finalized.`}
        </Text>

        {/* One or two signature cards */}
        {signatureCards}
      </View>
    </SectionCard>
  );
};

export default FinalSignatures;
